import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Clock, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-user work schedule editor — shown in Settings → Profile.
 * Lets the user set their start_time_local, workday_hours, and holiday_region.
 * Backend enforces: HH:MM 24h, 0.5–24h range, region ∈ {india, canada}.
 */
export default function WorkScheduleCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/user/work-schedule`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(`Failed to load schedule: ${e.message || 'unknown'}`);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/user/work-schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify(patch),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${r.status}`); }
      setData(await r.json());
      toast.success('Work schedule updated');
    } catch (e) {
      toast.error(`Save failed: ${e.message || 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const ws = data.work_schedule || {};

  return (
    <Card data-testid="work-schedule-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4 text-gold" /> Work schedule
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
          These control when you get a clock-in nudge if you forget and when your clock-out reminder fires.
          Set once; tweak anytime. Times are in your local timezone ({data.timezone}).
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-medium block mb-1">Start time (HH:MM)</label>
            <Input
              type="time"
              defaultValue={ws.start_time_local || '10:00'}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== ws.start_time_local)
                  save({ start_time_local: e.target.value });
              }}
              data-testid="work-schedule-start-time"
            />
            <p className="text-[10px] text-muted-foreground/60 mt-1">
              Defaults to 10:00. Reminder fires at {ws.start_time_local || '10:00'} + 10 min if not clocked in.
            </p>
          </div>
          <div>
            <label className="text-[11px] font-medium block mb-1">Workday hours</label>
            <Input
              type="number"
              step="0.5" min="0.5" max="24"
              defaultValue={ws.workday_hours || 8}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v) && v !== ws.workday_hours) save({ workday_hours: v });
              }}
              data-testid="work-schedule-hours"
            />
            <p className="text-[10px] text-muted-foreground/60 mt-1">
              Clock-out reminder fires at clock-in + {ws.workday_hours || 8}h + 15 min.
            </p>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-medium block mb-1 flex items-center gap-1.5">
            <Globe className="h-3 w-3" /> Holiday region
          </label>
          <div className="flex gap-2">
            {['india', 'canada'].map((r) => (
              <button
                key={r}
                onClick={() => save({ holiday_region: r })}
                disabled={busy}
                className={
                  'px-3 py-1 rounded-md border text-[12px] capitalize transition-colors ' +
                  (data.holiday_region === r
                    ? 'bg-gold text-black border-gold'
                    : 'border-border/30 text-muted-foreground/80 hover:text-gold')
                }
                data-testid={`work-schedule-region-${r}`}
              >
                {r}
              </button>
            ))}
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/60 ml-2" />}
          </div>
          <p className="text-[10px] text-muted-foreground/60 mt-1">
            Determines which pre-loaded holidays apply to your reminders / auto-popup.
            Currently: <strong className="text-foreground/80 capitalize">{data.holiday_region}</strong>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
